import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { memberApi } from '@formosa/shared/api/member';
import { resolveNewsApiLang } from '@formosa/shared/utils/newsLang';
import { useTranslation } from '../../i18n/LocaleContext';

export default function HelpCenterListPage() {
  const { locale, t } = useTranslation();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');
      try {
        const data = await memberApi.helpCenterList({ lang: resolveNewsApiLang(locale) });
        if (!cancelled) {
          setRecords(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || t('helpCenter.loadFailed'));
          setRecords([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [locale, t]);

  const filteredRecords = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) {
      return records;
    }
    return records.filter((item) => item.title?.toLowerCase().includes(q));
  }, [keyword, records]);

  return (
    <div className="help-center-page">
      <header className="help-center-page__header">
        <Link to="/" className="help-center-page__back" aria-label={t('common.back')}>
          ‹
        </Link>
        <h1 className="help-center-page__title">{t('helpCenter.title')}</h1>
      </header>

      <section className="help-center-page__hero">
        <div className="help-center-page__hero-text">
          <span className="help-center-page__hero-icon" aria-hidden>
            ?
          </span>
          <h2>{t('helpCenter.title')}</h2>
        </div>
        <div className="help-center-page__search">
          <input
            type="search"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder={t('helpCenter.searchPlaceholder')}
            aria-label={t('helpCenter.searchPlaceholder')}
          />
          <span className="help-center-page__search-icon" aria-hidden>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
              <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </span>
        </div>
      </section>

      <section className="help-center-page__card">
        <h3 className="help-center-page__section-title">
          <span className="help-center-page__section-accent" aria-hidden />
          {t('helpCenter.faqTitle')}
        </h3>

        {loading ? <p className="help-center-page__status">{t('common.loading')}</p> : null}
        {error ? <p className="help-center-page__status help-center-page__status--error">{error}</p> : null}
        {!loading && !error && filteredRecords.length === 0 ? (
          <p className="help-center-page__status">{t('helpCenter.empty')}</p>
        ) : null}

        <ul className="help-center-list">
          {filteredRecords.map((item) => (
            <li key={item.id}>
              <Link to={`/help/${item.id}`} className="help-center-list__item">
                <span className="help-center-list__icon" aria-hidden>?</span>
                <span className="help-center-list__title">{item.title}</span>
                <span className="help-center-list__chevron" aria-hidden>›</span>
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
